import { useState } from "react";
import { nextElementId, useDesignerStore } from "../store/useDesignerStore";
import type { PrintableTableOption, ReportTypeOption } from "../types";

interface Props {
  reportTypes: ReportTypeOption[];
  selectedReportType: string;
  onReportTypeChange: (reportType: string) => void;
}

export function Toolbox({ reportTypes, selectedReportType, onReportTypeChange }: Props) {
  const addElement = useDesignerStore((s) => s.addElement);

  const selectedOption = reportTypes.find((rt) => rt.key === selectedReportType);
  const fields = selectedOption?.available_fields ?? [];
  const tables = selectedOption?.available_tables ?? [];

  const [fieldToAdd, setFieldToAdd] = useState(fields[0]?.key ?? "");
  const [tableToAdd, setTableToAdd] = useState<PrintableTableOption | undefined>(tables[0]);

  // Keep the local "field to add" selection valid when the report type (and
  // therefore the available fields) changes out from under us.
  if (fields.length > 0 && !fields.some((f) => f?.key === fieldToAdd)) {
    setFieldToAdd(fields[0]?.key ?? "");
  }

  return (
    <aside style={{ width: 270, borderInlineEnd: "1px solid #e5e7eb", padding: 12, direction: "rtl", boxSizing: "border-box" }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>نوع گزارش</h3>
      <select
        data-testid="report-type-select"
        value={selectedReportType}
        onChange={(e) => onReportTypeChange(e.target.value)}
        style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 16 }}
      >
        {reportTypes.map((rt) => (
          <option key={rt.key} value={rt.key}>
            {rt.label}
          </option>
        ))}
      </select>

      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>افزودن المان</h3>

      {/* --- Static Text --- */}
      <button
        type="button"
        data-testid="add-text-btn"
        onClick={() =>
          addElement({ id: nextElementId(), type: "text", content: "متن جدید", x: 0, y: 0, w: 3, h: 1 })
        }
        style={{ width: "100%", padding: "8px 12px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", marginBottom: 16, textAlign: "center", fontWeight: 500 }}
      >
        + متن ثابت
      </button>

      {/* --- Fields Dropdown & Button --- */}
      <div style={{ marginBottom: 16, padding: 10, background: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6" }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>فیلدهای داده (ثابت و سفارشی)</label>
        {fields.length === 0 ? (
          <p data-testid="fields-empty-state" style={{ fontSize: 11, color: "#9ca3af" }}>
            فیلدهای این نوع گزارش هنوز تعریف نشده‌اند.
          </p>
        ) : (
          <div>
            <select
              data-testid="field-select"
              value={fieldToAdd}
              onChange={(e) => setFieldToAdd(e.target.value)}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 8, fontSize: 12 }}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="add-data-field-btn"
              onClick={() =>
                addElement({
                  id: nextElementId(),
                  type: "data_field",
                  field_key: fieldToAdd,
                  x: 0,
                  y: 0,
                  w: 3,
                  h: 1,
                })
              }
              style={{ width: "100%", padding: "6px 12px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
            >
              + افزودن فیلد انتخاب‌شده
            </button>
          </div>
        )}
      </div>

      {/* --- Tables Dropdown & Button --- */}
      <div style={{ marginBottom: 16, padding: 10, background: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6" }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>جدول‌های وابسته (BOM و ...)</label>
        {tables.length === 0 ? (
          <p data-testid="tables-empty-state" style={{ fontSize: 11, color: "#9ca3af" }}>
            جدولی برای این نوع گزارش تعریف نشده است.
          </p>
        ) : (
          <div>
            <select
              data-testid="table-select"
              value={tableToAdd?.key ?? ""}
              onChange={(e) => setTableToAdd(tables.find((t) => t.key === e.target.value))}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 8, fontSize: 12 }}
            >
              {tables.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="add-table-btn"
              disabled={!tableToAdd}
              onClick={() => {
                if (!tableToAdd) return;
                addElement({
                  id: nextElementId(),
                  type: "table",
                  field_key: tableToAdd.key,
                  columns: tableToAdd.columns.map((c) => c.key),
                  x: 0,
                  y: 0,
                  w: 12,
                  h: 5,
                });
              }}
              style={{ width: "100%", padding: "6px 12px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
            >
              + افزودن جدول انتخاب‌شده
            </button>
          </div>
        )}
      </div>

      {/* --- Row Number Button --- */}
      <button
        type="button"
        data-testid="add-row-number-btn"
        onClick={() =>
          addElement({ id: nextElementId(), type: "row_number", start_from: 1, x: 0, y: 0, w: 1, h: 1 })
        }
        style={{ width: "100%", padding: "8px 12px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", marginBottom: 12, textAlign: "center", fontWeight: 500 }}
      >
        + شماره ردیف (خودکار)
      </button>

      {/* --- Group Repeater Button --- */}
      <button
        type="button"
        data-testid="add-group-btn"
        onClick={() => {
          const repeatSource = tables[0]?.key ?? "items";
          addElement({
            id: nextElementId(),
            type: "group",
            repeat_source: repeatSource,
            row_height: 1,
            children: [],
            x: 0,
            y: 0,
            w: 12,
            h: 3,
          });
        }}
        style={{ width: "100%", padding: "8px 12px", background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 6, cursor: "pointer", marginBottom: 16, textAlign: "center", fontWeight: 600 }}
      >
        + گروه تکرارکننده ردیف (Repeater)
      </button>

      {/* --- Shapes Buttons --- */}
      <div style={{ marginBottom: 16, padding: 10, background: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6" }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>اشکال هندسی و خطوط</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <button
            type="button"
            data-testid="add-line-h-btn"
            onClick={() =>
              addElement({ id: nextElementId(), type: "shape", shape_kind: "line_h", stroke_width: 1, stroke_color: "#000000", stroke_style: "solid", x: 0, y: 0, w: 6, h: 1 })
            }
            style={{ padding: "6px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
          >
            ― خط افقی
          </button>
          <button
            type="button"
            data-testid="add-line-v-btn"
            onClick={() =>
              addElement({ id: nextElementId(), type: "shape", shape_kind: "line_v", stroke_width: 1, stroke_color: "#000000", stroke_style: "solid", x: 0, y: 0, w: 1, h: 6 })
            }
            style={{ padding: "6px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
          >
            | خط عمودی
          </button>
        </div>
        <button
          type="button"
          data-testid="add-rect-btn"
          onClick={() =>
            addElement({ id: nextElementId(), type: "shape", shape_kind: "rect", stroke_width: 1, stroke_color: "#000000", stroke_style: "solid", fill_color: "transparent", x: 0, y: 0, w: 4, h: 3 })
          }
          style={{ width: "100%", padding: "6px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
        >
          ▢ کادر / مستطیل
        </button>
      </div>

      {/* --- Barcode & QR Code --- */}
      <div style={{ marginBottom: 16, padding: 10, background: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6" }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>بارکد و شناسه (Barcode & QR)</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <button
            type="button"
            data-testid="add-barcode-btn"
            onClick={() =>
              addElement({
                id: nextElementId(),
                type: "barcode",
                field_key: fieldToAdd || undefined,
                content: "12345678",
                show_text: true,
                x: 0,
                y: 0,
                w: 6,
                h: 2,
              })
            }
            style={{ padding: "6px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 500 }}
          >
            ❚❙❘ بارکد میله‌ای
          </button>
          <button
            type="button"
            data-testid="add-qrcode-btn"
            onClick={() =>
              addElement({
                id: nextElementId(),
                type: "qr_code",
                field_key: fieldToAdd || undefined,
                content: "https://mimzad.ir",
                x: 0,
                y: 0,
                w: 3,
                h: 3,
              })
            }
            style={{ padding: "6px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 500 }}
          >
            ▣ بارکد ۲ بعدی (QR)
          </button>
        </div>
      </div>

      {/* --- Image Button --- */}
      <button
        type="button"
        data-testid="add-image-btn"
        onClick={() => addElement({ id: nextElementId(), type: "image", src: "", x: 0, y: 0, w: 3, h: 3 })}
        style={{ width: "100%", padding: "8px 12px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", textAlign: "center", fontWeight: 500 }}
      >
        + تصویر
      </button>
    </aside>
  );
}
